import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { DataTable, type TanstackReactTable, confirm, toast } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import type { CatalogCandidateRow } from "../api/client";
import { CatalogMergeModal } from "./CatalogMergeModal";
import { useState } from "react";

type CatalogCandidatesTableProps = {
  isLoading: boolean;
  data: CatalogCandidateRow[];
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  onApprove?: (catalogProductId: string) => Promise<void>;
  onReject?: (catalogProductId: string) => Promise<void>;
  canManage?: boolean;
};

export function CatalogCandidatesTable({
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  onApprove,
  onReject,
  canManage = false,
}: CatalogCandidatesTableProps) {
  const [mergeSourceId, setMergeSourceId] = useState<string | null>(null);
  const [mergeSourceName, setMergeSourceName] = useState("");

  const columns: TanstackReactTable.ColumnDef<CatalogCandidateRow>[] = [
    {
      id: "order",
      header: "ລຳດັບ",
      cell: ({ row }) => offset + row.index + 1,
      size: 70,
    },
    {
      id: "productName",
      header: "ຊື່ສິນຄ້າ",
      cell: ({ row }) => (
        <span className="font-medium">{row.original.productName}</span>
      ),
      size: 240,
    },
    {
      id: "oemPartNo",
      header: "OEM",
      cell: ({ row }) => row.original.oemPartNo ?? "—",
      size: 120,
    },
    {
      id: "shop",
      header: "ຮ້ານເສີຍ",
      cell: ({ row }) => row.original.proposedByShopName ?? "—",
      size: 160,
    },
    {
      id: "updatedAt",
      header: "ອັບເດດ",
      cell: ({ row }) => formatDateTimeLocal(row.original.updatedAt),
      size: 140,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex flex-wrap items-center gap-2">
          {canManage && onApprove ? (
            <button
              type="button"
              className="text-primary text-sm font-medium hover:underline"
              onClick={async () => {
                const ok = await confirm({
                  title: "ອະນຸມັດ candidate",
                  description:
                    "ເປີດ master ເປັນ active — ຮ້ານຂາຍໄດ້ · fitment ຍັງຄິວຕາມຄວາມຈຳເປັນ",
                  actionText: "ອະນຸມັດ",
                });
                if (!ok) return;
                try {
                  await onApprove(row.original.catalogProductId);
                } catch {
                  toast.error("ອະນຸມັດລົ້ມເຫຼວ");
                }
              }}
            >
              ອະນຸມັດ
            </button>
          ) : null}
          {canManage ? (
            <button
              type="button"
              className="text-sm hover:underline"
              onClick={() => {
                setMergeSourceId(row.original.catalogProductId);
                setMergeSourceName(row.original.productName);
              }}
            >
              Merge…
            </button>
          ) : null}
          <Link
            to="/app/catalog/$id"
            params={{ id: row.original.catalogProductId }}
            className="text-muted-foreground text-sm hover:underline"
          >
            ເບິ່ງ
          </Link>
          {canManage && onReject ? (
            <button
              type="button"
              className="text-destructive text-sm hover:underline"
              onClick={async () => {
                const ok = await confirm({
                  title: "ປະຕິເສດ candidate",
                  description: "archive master ແລະ ປິດ offer ຂອງຮ້ານ",
                  actionText: "ປະຕິເສດ",
                  ActionProps: { variant: "destructive" },
                });
                if (!ok) return;
                try {
                  await onReject(row.original.catalogProductId);
                } catch {
                  toast.error("ປະຕິເສດລົ້ມເຫຼວ");
                }
              }}
            >
              ປະຕິເສດ
            </button>
          ) : null}
        </div>
      ),
      size: 260,
    },
  ];

  return (
    <>
      <DataTable<CatalogCandidateRow, unknown>
        noDataMessage="ບໍ່ມີ candidate ລໍຖ້າ"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      />
      {mergeSourceId ? (
        <CatalogMergeModal
          open={Boolean(mergeSourceId)}
          onOpenChange={(open) => {
            if (!open) {
              setMergeSourceId(null);
              setMergeSourceName("");
            }
          }}
          sourceProductId={mergeSourceId}
          sourceProductName={mergeSourceName}
          onMerged={() => {
            setMergeSourceId(null);
            setMergeSourceName("");
          }}
        />
      ) : null}
    </>
  );
}
